<template>
    <Wizard
        :isOpen="isOpen"
        :title="wizardTitle"
        title-id="sdk-wizard-title"
        :showBack="page === 'steps'"
        @close="$emit('close')"
        @back="page = 'picker'"
    >
        <!-- Success state -->
        <div
            v-if="connected"
            class="flex flex-col items-center justify-center h-full px-6 py-16 text-center"
        >
            <div
                class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4"
            >
                <svg
                    class="h-8 w-8 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                    />
                </svg>
            </div>
            <h3 class="text-xl font-medium text-taupe-900 mb-2">
                You're connected! 🎉
            </h3>
            <p class="text-taupe-600 mb-6">
                We detected your first flag evaluation. Your SDK is working
                perfectly.
            </p>
            <zen-button type="button" variant="primary" @click="$emit('close')">
                Continue to dashboard
            </zen-button>
        </div>

        <!-- Page 1: SDK picker -->
        <SdkPickerPage
            v-else-if="page === 'picker'"
            :sdks="sdks"
            @select="selectSdk"
        />

        <!-- Page 2: Steps -->
        <SdkStepsPage
            v-else-if="page === 'steps' && activeSdk"
            :sdk="activeSdk"
            :copied="copied"
            @copy="copy"
        />

        <!-- Waiting indicator footer (steps page only, not shown if already connected) -->
        <template
            v-if="!connected && !skipConnectionCheck && page === 'steps'"
            #footer
        >
            <div
                class="flex items-center gap-2 px-6 py-4 text-sm text-taupe-500"
            >
                <svg
                    class="h-4 w-4 animate-spin text-sky-500"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                    ></path>
                </svg>
                Waiting for your first flag evaluation&hellip;
            </div>
        </template>
    </Wizard>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import {
    siAndroid,
    siDotnet,
    siGo,
    siJavascript,
    siLaravel,
    siNextdotjs,
    siOpenjdk,
    siPhp,
    siPython,
    siReact,
    siSwift,
} from 'simple-icons'
import Wizard from './wizard/WizardDialog.vue'
import SdkPickerPage from './wizard/SdkPickerPage.vue'
import SdkStepsPage from './wizard/SdkStepsPage.vue'
import OnboardingService from '@/services/onboarding.service'
import { useOnboardingStore } from '@/stores/onboarding'

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    accountUlid: {
        type: String,
        default: null,
    },
    clientKey: {
        type: String,
        default: '',
    },
    mobileKey: {
        type: String,
        default: '',
    },
    serverKey: {
        type: String,
        default: '',
    },
})

defineEmits(['close'])

const onboardingStore = useOnboardingStore()

const page = ref('picker')
const activeSdk = ref(null)
const copied = ref(null)
const connected = ref(false)
const skipConnectionCheck = ref(false)

function isSdkAlreadyConnected() {
    return onboardingStore.steps.some(
        (s) => s.key === 'sdk_connected' && s.completed
    )
}

const sdks = computed(() => [
    {
        id: 'go',
        label: 'Go',
        group: 'Backend',
        icon: siGo,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-go#readme',
        install: 'go get github.com/zenmanage/zenmanage-go',
        init: `package main

import (
    "context"
    "fmt"
    "log"

    "github.com/zenmanage/zenmanage-go"
)

func main() {
    cfg, err := zenmanage.NewConfigBuilder().
        WithEnvironmentToken("${props.serverKey}").
        Build()
    if err != nil {
        log.Fatal(err)
    }

    client := zenmanage.New(cfg)

    enabled, err := client.IsEnabled(context.Background(), "first-boolean", "")
    if err != nil {
        log.Fatal(err)
    }
    fmt.Println("first-boolean:", enabled)
}`,
    },
    {
        id: 'java',
        label: 'Java',
        group: 'Backend',
        // simple-icons has no Java mark; OpenJDK is the closest it ships
        icon: siOpenjdk,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-java#readme',
        install: `<dependency>
  <groupId>com.zenmanage</groupId>
  <artifactId>zenmanage-java</artifactId>
  <version>1.0.0</version>
</dependency>`,
        init: `import com.zenmanage.sdk.Zenmanage;
import com.zenmanage.sdk.config.Config;
import com.zenmanage.sdk.config.ConfigBuilder;

Config config = ConfigBuilder.create()
    .withEnvironmentToken("${props.serverKey}")
    .build();

Zenmanage zenmanage = new Zenmanage(config);

boolean enabled = zenmanage.flags()
    .single("first-boolean", false)
    .isEnabled();
System.out.println("first-boolean: " + enabled);`,
    },
    {
        id: 'laravel',
        label: 'Laravel',
        group: 'Backend',
        icon: siLaravel,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-laravel#readme',
        install: 'composer require zenmanage/zenmanage-laravel',
        init: `# In your .env file:
ZENMANAGE_ENVIRONMENT_TOKEN=${props.serverKey}

# In your controller or service:
use Zenmanage\\Laravel\\Facades\\Zenmanage;

$value = Zenmanage::single('first-boolean')->isEnabled();`,
    },
    {
        id: 'dotnet',
        label: '.NET',
        group: 'Backend',
        icon: siDotnet,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-dotnet#readme',
        install: 'dotnet add package Zenmanage',
        init: `using Zenmanage;

var zenmanage = new Zenmanage.Zenmanage(
    ConfigBuilder.Create()
        .WithEnvironmentToken("${props.serverKey}")
        .Build());

var flag = await zenmanage.Flags().SingleAsync("first-boolean");
Console.WriteLine(flag.IsEnabled());`,
    },
    {
        id: 'php',
        label: 'PHP',
        group: 'Backend',
        icon: siPhp,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-php#readme',
        install: 'composer require zenmanage/zenmanage-php',
        init: `use Zenmanage\\Config\\ConfigBuilder;
use Zenmanage\\Zenmanage;

$zenmanage = new Zenmanage(
    ConfigBuilder::create()
        ->withEnvironmentToken('${props.serverKey}')
        ->build()
);

$value = $zenmanage->flags()->single('first-boolean')->isEnabled();
var_dump($value);`,
    },
    {
        id: 'python',
        label: 'Python',
        group: 'Backend',
        icon: siPython,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-python#readme',
        install: 'pip install zenmanage',
        init: `from zenmanage import ConfigBuilder, Zenmanage

zenmanage = Zenmanage(
    ConfigBuilder.create()
    .with_environment_token("${props.serverKey}")
    .build()
)

flag = zenmanage.flags().single("first-boolean", False)
print(flag.is_enabled())`,
    },
    {
        id: 'javascript',
        label: 'JavaScript',
        group: 'Web',
        icon: siJavascript,
        keyType: 'client',
        readme: 'https://github.com/zenmanage/zenmanage-javascript#readme',
        install: 'npm install @zenmanage/sdk',
        init: `import { Zenmanage, ConfigBuilder } from '@zenmanage/sdk';

const zenmanage = new Zenmanage(
  ConfigBuilder.create()
    .withEnvironmentToken('${props.clientKey}')
    .build()
);

const value = await zenmanage.flags().single('first-boolean').value();
console.log('first-boolean:', value);`,
    },
    {
        id: 'nextjs',
        label: 'Next.js',
        group: 'Web',
        icon: siNextdotjs,
        keyType: 'server',
        readme: 'https://github.com/zenmanage/zenmanage-nextjs#readme',
        install:
            'npm install @zenmanage/nextjs @zenmanage/react @zenmanage/sdk',
        init: `// app/page.tsx (a Server Component)
import { getServerFlag } from '@zenmanage/nextjs';

export default async function Page() {
  const flag = await getServerFlag({
    // Server key: keep it in server code, and move it to an environment
    // variable before you commit.
    environmentToken: '${props.serverKey}',
    key: 'first-boolean',
    defaultValue: false,
  });

  return <p>first-boolean: {String(flag.asBool())}</p>;
}`,
    },
    {
        id: 'react',
        label: 'React',
        group: 'Web',
        icon: siReact,
        keyType: 'client',
        readme: 'https://github.com/zenmanage/zenmanage-react#readme',
        install: 'npm install @zenmanage/react @zenmanage/sdk react',
        init: `import { FlagsProvider, useFlag } from '@zenmanage/react';

function FirstBoolean() {
  const { value, isLoading } = useFlag('first-boolean', false);

  if (isLoading) return <p>Loading...</p>;
  return <p>first-boolean: {String(value)}</p>;
}

export function App() {
  return (
    <FlagsProvider environmentToken="${props.clientKey}">
      <FirstBoolean />
    </FlagsProvider>
  );
}`,
    },
    {
        id: 'android',
        label: 'Android',
        group: 'Mobile',
        icon: siAndroid,
        keyType: 'mobile',
        readme: 'https://github.com/zenmanage/zenmanage-kotlin#readme',
        install: `// build.gradle.kts
dependencies {
    implementation("com.zenmanage:zenmanage-kotlin:1.0.0")
}`,
        init: `import com.zenmanage.kotlin.Zenmanage
import com.zenmanage.kotlin.config.ConfigBuilder

val config = ConfigBuilder.create()
    .withEnvironmentToken("${props.mobileKey}")
    .build()

val zenmanage = Zenmanage(config)

val enabled = zenmanage.flags().single("first-boolean", false).isEnabled()
println("first-boolean: $enabled")`,
    },
    {
        id: 'ios',
        label: 'iOS',
        group: 'Mobile',
        icon: siSwift,
        keyType: 'mobile',
        readme: 'https://github.com/zenmanage/zenmanage-swift#readme',
        install: `// In Xcode: File > Add Package Dependencies, or in Package.swift:
.package(url: "https://github.com/zenmanage/zenmanage-swift.git", from: "1.0.0")`,
        init: `import Zenmanage

let zenmanage = try Zenmanage(environmentToken: "${props.mobileKey}")

let flag = try await zenmanage.flags().single("first-boolean", defaultValue: false)
print("first-boolean:", flag.isEnabled())`,
    },
    {
        id: 'react-native',
        label: 'React Native',
        group: 'Mobile',
        icon: siReact,
        keyType: 'mobile',
        readme: 'https://github.com/zenmanage/zenmanage-react-native#readme',
        install: 'npm install @zenmanage/react-native @zenmanage/sdk react react-native',
        init: `import { Text } from 'react-native';
import { FlagsProvider, useFlag } from '@zenmanage/react-native';

function FirstBoolean() {
  const { value, isLoading } = useFlag('first-boolean', false);

  if (isLoading) return <Text>Loading...</Text>;
  return <Text>first-boolean: {String(value)}</Text>;
}

export function App() {
  return (
    <FlagsProvider environmentToken="${props.mobileKey}">
      <FirstBoolean />
    </FlagsProvider>
  );
}`,
    },
])

const wizardTitle = computed(() => {
    if (connected.value) return 'Connected!'
    if (page.value === 'steps') return activeSdk.value.label
    return 'Connect your SDK'
})

function selectSdk(sdk) {
    activeSdk.value = sdk
    page.value = 'steps'
}

function copy(slot, text) {
    void navigator.clipboard.writeText(text).then(() => {
        copied.value = slot
        setTimeout(() => {
            if (copied.value === slot) copied.value = null
        }, 2000)
    })
}

let pollTimer = null

function startPolling() {
    if (!props.accountUlid) return
    pollTimer = setInterval(() => {
        void (async () => {
            try {
                const response = await OnboardingService.status(
                    props.accountUlid
                )
                const milestones = response?.data ?? []
                const sdkMilestone = milestones.find(
                    (m) => m.key === 'sdk_connected'
                )
                if (sdkMilestone?.completed) {
                    connected.value = true
                    stopPolling()
                }
            } catch {
                // silently ignore polling errors
            }
        })()
    }, 3000)
}

function stopPolling() {
    clearInterval(pollTimer)
    pollTimer = null
}

watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            page.value = 'picker'
            activeSdk.value = null
            copied.value = null
            if (isSdkAlreadyConnected()) {
                skipConnectionCheck.value = true
            } else if (!connected.value) {
                skipConnectionCheck.value = false
                startPolling()
            }
        } else {
            stopPolling()
        }
    },
    { immediate: true }
)

onUnmounted(stopPolling)
</script>
